import { useEffect } from 'react';

interface SeoMetaOptions {
  title: string;
  description: string;
  keywords?: string;
  lang: 'en' | 'tr';
  canonicalPath?: string;
  ogLocale?: string;
  ogImage?: string;
}

const SITE_URL = 'https://www.canozen.com.tr';
const DEFAULT_OG_IMAGE =
  'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/7fe880b7ed4d0915e58be95dc2eb2824.jfif';

function setMeta(selector: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    // parse attr from selector e.g. meta[name="description"] → name="description"
    const match = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string, extra?: Record<string, string>) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  if (extra) Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
}

export function useSeoMeta({
  title,
  description,
  keywords,
  lang,
  canonicalPath = '',
  ogLocale,
  ogImage = DEFAULT_OG_IMAGE,
}: SeoMetaOptions) {
  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const locale = ogLocale ?? (lang === 'tr' ? 'tr_TR' : 'en_US');
    const langAttr = lang === 'tr' ? 'tr-TR' : 'en';

    // 1. Document title
    document.title = title;

    // 2. <html lang>
    document.documentElement.setAttribute('lang', langAttr);

    // 3. Primary meta
    setMeta('meta[name="title"]', title);
    setMeta('meta[name="description"]', description);
    if (keywords) setMeta('meta[name="keywords"]', keywords);
    setMeta('meta[name="language"]', lang === 'tr' ? 'Turkish' : 'English');
    setMeta('meta[name="Last-modified"]', new Date().toISOString().split('T')[0]);

    // 4. Content-Language
    let clMeta = document.querySelector<HTMLMetaElement>('meta[http-equiv="Content-Language"]');
    if (!clMeta) {
      clMeta = document.createElement('meta');
      clMeta.setAttribute('http-equiv', 'Content-Language');
      document.head.appendChild(clMeta);
    }
    clMeta.setAttribute('content', lang === 'tr' ? 'tr' : 'en');

    // 5. Canonical
    setLink('canonical', canonicalUrl);

    // 6. Open Graph
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:locale"]', locale);
    setMeta('meta[property="og:image"]', ogImage);
    setMeta('meta[property="og:image:secure_url"]', ogImage);
    setMeta('meta[property="og:image:width"]', '1200');
    setMeta('meta[property="og:image:height"]', '630');
    setMeta('meta[property="og:image:alt"]', 'CSG The Event Company — Professional AV Production');
    setMeta('meta[property="og:site_name"]', 'CSG The Event Company — Can ÖZEN Technical Production');
    setMeta('meta[property="og:type"]', 'website');

    // 7. Twitter / X
    setMeta('meta[name="twitter:card"]', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', ogImage);
    setMeta('meta[name="twitter:image:alt"]', 'CSG The Event Company — Professional AV Production');
    setMeta('meta[name="twitter:url"]', canonicalUrl);

    // 8. Geo (always Istanbul as HQ)
    setMeta('meta[name="geo.region"]', 'TR-34');
    setMeta('meta[name="geo.placename"]', lang === 'tr' ? 'Beyoğlu, İstanbul, Türkiye' : 'Beyoglu, Istanbul, Turkey');
    setMeta('meta[name="geo.position"]', '41.0082;28.9784');
    setMeta('meta[name="ICBM"]', '41.0082, 28.9784');

    // 9. hreflang alternates
    document.querySelectorAll('link[rel="alternate"]').forEach((el) => el.remove());
    [
      { hreflang: 'x-default', href: `${SITE_URL}/` },
      { hreflang: 'en', href: `${SITE_URL}/` },
      { hreflang: 'tr', href: `${SITE_URL}/tr` },
    ].forEach(({ hreflang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      document.head.appendChild(link);
    });
  }, [title, description, keywords, lang, canonicalPath, ogLocale, ogImage]);
}
